/**
 * Atomic snapshot of the meeting mic-drop gate.
 *
 * Published by the meeting AX poller in the main process
 * (`MeetingDetectionService`) on the single {@link MEETING_MIC_GATE_KEY}
 * GlobalState key, and consumed by the renderer audio pipeline
 * (`AudioTranscriptionService` → `AudioChannel`) and the meeting mic-status
 * indicator.
 *
 * Carrying every field in ONE value (rather than independent per-signal keys)
 * keeps them atomically consistent: every GlobalState write is a single object,
 * so the renderer can never observe a transient mix of a fresh self-mute with a
 * stale capture-override flag (or vice versa). That deletes the cross-key
 * publish-order contract in the main process and the listener-order contract in
 * the renderer — the mic can't be left open on a muted user just because two
 * signals landed on different ticks.
 *
 * The publisher precomputes {@link MeetingMicGate.shouldDropMic} so the gate is
 * a single boolean read with no cross-field logic at the capture hot path.
 */
export interface MeetingMicGate {
  /**
   * Self-mute on the active call.
   *   `true`  — muted, `false` — unmuted,
   *   `null`  — undetermined; consumers MUST treat this as "do not drop" so an
   *             AX hiccup can't silently kill capture.
   */
  selfMute: boolean | null
  /**
   * Google Meet "merged audio" (Companion mode) — laptop joined alongside a
   * nearby room device, so Meet routes the user's voice through that device
   * rather than the laptop mic. `false` for every other app and the normal,
   * non-merged Meet case.
   */
  mergedAudio: boolean
  /**
   * The user's explicit opt-in to keep recording while the call shows them
   * muted — per-call ("Record anyway") or the Companion-mode setting.
   */
  captureWhileMuted: boolean
  /**
   * Precomputed gate decision: `selfMute === true && !captureWhileMuted`. The
   * mic capture loop zeroes host-channel PCM frames iff this is `true`.
   */
  shouldDropMic: boolean
}

/** GlobalState key carrying the active meeting's {@link MeetingMicGate}. */
export const MEETING_MIC_GATE_KEY = 'meeting-mic-gate'

/**
 * Renderer → main "Record anyway" command. A command, not shared state: only
 * the gate publisher reads it, and its effect returns on the gate itself.
 */
export const MEETING_MIC_CAPTURE_OVERRIDE_EVENT = 'meeting:set-mic-capture-override'

/**
 * Neutral gate: no call / undetermined → never drop, show no indicator. Used
 * as the read default and as the cleared value when a meeting deactivates.
 */
export const NEUTRAL_MEETING_MIC_GATE: MeetingMicGate = {
  selfMute: null,
  mergedAudio: false,
  captureWhileMuted: false,
  shouldDropMic: false,
}

/**
 * Build a gate from the raw signals, deriving the drop decision in ONE place so
 * `shouldDropMic` can never disagree with its inputs. The publisher resolves
 * `captureWhileMuted` via {@link resolveCaptureWhileMuted}.
 */
export function buildMeetingMicGate(
  selfMute: boolean | null,
  mergedAudio: boolean,
  captureWhileMuted = false,
): MeetingMicGate {
  return {
    selfMute,
    mergedAudio,
    captureWhileMuted,
    shouldDropMic: selfMute === true && !captureWhileMuted,
  }
}

/**
 * A per-call choice always wins; `null` (no choice this call) falls back to the
 * Companion-mode setting, which only applies while Meet is merging audio — the
 * one case where a muted laptop mic is still the only path to the user's voice.
 */
export function resolveCaptureWhileMuted(
  mergedAudio: boolean,
  perCallOverride: boolean | null,
  captureWhileMergedMuted: boolean,
): boolean {
  return perCallOverride ?? (mergedAudio && captureWhileMergedMuted)
}

/**
 * Coerce an arbitrary GlobalState value (which crosses the main→renderer
 * boundary as plain JSON) into a well-formed {@link MeetingMicGate}. The drop
 * decision is always re-derived via {@link buildMeetingMicGate}, so a malformed
 * or partial payload fails safe to "do not drop" rather than holding the gate
 * open or shut on a stale field.
 */
export function normalizeMeetingMicGate(value: unknown): MeetingMicGate {
  if (!value || typeof value !== 'object') return NEUTRAL_MEETING_MIC_GATE
  const v = value as Partial<MeetingMicGate>
  const selfMute = v.selfMute === true ? true : v.selfMute === false ? false : null
  return buildMeetingMicGate(selfMute, v.mergedAudio === true, v.captureWhileMuted === true)
}

/** Structural equality so publishers/listeners can skip redundant work. */
export function meetingMicGatesEqual(a: MeetingMicGate, b: MeetingMicGate): boolean {
  return (
    a.selfMute === b.selfMute &&
    a.mergedAudio === b.mergedAudio &&
    a.captureWhileMuted === b.captureWhileMuted &&
    a.shouldDropMic === b.shouldDropMic
  )
}

/**
 * Fold an indeterminate (`null`) self-mute reading onto the last definite one.
 *
 * The AX poller intermittently returns `selfMute: null` for a single tick on a
 * live call — Google Meet briefly drops the toolbar mic button from the AX tree
 * (its controls auto-hide), or a whole `getMeetingState` scan comes back empty
 * between two good ticks. Treating each such `null` as "do not drop" reopened
 * the mic mid-call and transcribed a *muted* user for the duration of the blip
 * (the field bug this fixes). Once we've positively observed the mute state, a
 * `null` tick should HOLD it rather than discard it: an absence of evidence is
 * not evidence of un-mute.
 *
 * Fail-open is preserved exactly where it still matters: if we've never read a
 * definite value (`last === null` — cold start, or AX that never worked) the
 * result stays `null`, so the gate can't silently kill capture on a user we've
 * never confirmed muted. Only a confirmed `true`/`false` ever latches, and a
 * later confirmed reading always wins over the latch (so an un-mute releases
 * the drop immediately). The latch must be reset when a *new* meeting/recording
 * session begins so a prior call's mute can't bleed into the next.
 */
export function latchSelfMute(current: boolean | null, last: boolean | null): boolean | null {
  return current !== null ? current : last
}
